import { useEffect, useRef, useState } from "react";
import { values } from "../../../mocks/home";

const REAL_PHOTO_3 = "https://storage.readdy-site.link/project_files/1dd47190-5ea2-431c-a9ac-432354462ac6/b9139997-39df-458b-8486-4b43cdfa9a0b_5224464aa9bd985acaa9b906992659a1539a3762.png?v=f0018e23e213c3c471b0f64377da771f";

export default function ValuesSection() {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-[#F2F4F6] py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-6">

        {/* Two-column layout: photo left, cards right */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 items-start">

          {/* Left: header + featured photo */}
          <div
            className={`lg:w-[420px] flex-shrink-0 transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            {/* Header */}
            <div className="mb-8">
              <span className="text-xs font-semibold tracking-widest text-[#191F28]/40 uppercase mb-4 block">
                GrowSeo의 핵심 가치
              </span>
              <h2 className="text-4xl lg:text-5xl font-black text-[#191F28] leading-relaxed lg:leading-relaxed">
                N잡. 칼퇴.
                <br />
                자동화.
              </h2>
              <p className="mt-5 text-lg text-[#191F28]/60 leading-[1.9]">
                AI로 돈 버는 법, 시간 버는 법을 2030이 원하는 방식으로.
              </p>
            </div>

            {/* Featured photo */}
            <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: "3/4" }}>
              <img
                src={REAL_PHOTO_3}
                alt="GrowSeo 실제 학습 현장"
                className="w-full h-full object-cover object-top"
              />
              {/* Bottom gradient overlay with label */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pt-16 pb-5 px-5">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00D873] rounded-full animate-pulse" />
                  <span className="text-white/80 text-xs font-semibold tracking-wide">실제 수강생 현장</span>
                </div>
                <p className="text-white font-bold text-sm mt-1 leading-normal">
                  매일 성장하는 12,400명의 이야기
                </p>
              </div>
            </div>
          </div>

          {/* Right: 3 value cards stacked */}
          <div className="flex-1 flex flex-col gap-5 lg:pt-[108px]">
            {values.map((v, i) => (
              <div
                key={v.id}
                className={`group bg-white rounded-3xl p-7 cursor-pointer transition-all duration-500 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                } ${hovered === v.id ? "-translate-y-1" : ""}`}
                style={{ transitionDelay: `${i * 120 + 200}ms` }}
                onMouseEnter={() => setHovered(v.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="flex items-start gap-5">
                  {/* Icon */}
                  <div
                    className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-2xl transition-all duration-300"
                    style={{
                      backgroundColor: hovered === v.id ? v.color : `${v.color}15`,
                    }}
                  >
                    <i
                      className={`${v.icon} text-xl transition-colors duration-300`}
                      style={{ color: hovered === v.id ? "#fff" : v.color }}
                    />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    {/* Tag */}
                    <div
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold mb-2"
                      style={{ backgroundColor: `${v.color}20`, color: v.color }}
                    >
                      {v.tag}
                    </div>
                    <h3 className="text-lg font-black text-[#191F28] mb-1.5 leading-normal">{v.title}</h3>
                    <p className="text-[#191F28]/60 leading-[1.8] text-sm">{v.desc}</p>
                  </div>

                  {/* Arrow */}
                  <div
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 mt-1"
                    style={{
                      backgroundColor: hovered === v.id ? `${v.color}20` : "transparent",
                      color: v.color,
                    }}
                  >
                    <i className="ri-arrow-right-line text-sm transition-transform duration-200 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
